import React, { useState, useEffect } from 'react';
import { useAdminAuth } from '../context/AdminAuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  RefreshCw, 
  ChevronLeft, 
  ChevronRight, 
  AlertTriangle,
  Search,
  ShieldCheck,
  Clock,
  Filter
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

const ADMIN_API_URL = `${process.env.REACT_APP_BACKEND_URL}/api/secure-admin`;

const EVENT_TYPE_COLORS = {
  'admin.login': 'bg-green-600/20 text-green-400',
  'admin.logout': 'bg-gray-600/20 text-gray-400',
  'user.suspend': 'bg-red-600/20 text-red-400',
  'user.unsuspend': 'bg-green-600/20 text-green-400',
  'listing.hide': 'bg-yellow-600/20 text-yellow-400',
  'listing.remove': 'bg-red-600/20 text-red-400',
  'config.update': 'bg-blue-600/20 text-blue-400',
  'payment.refund': 'bg-orange-600/20 text-orange-400',
  'ai.': 'bg-purple-600/20 text-purple-400',
};

export const AdminAuditLogPage = () => {
  const { token } = useAdminAuth();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [eventType, setEventType] = useState('');
  const [offset, setOffset] = useState(0);
  const [integrity, setIntegrity] = useState(null);
  const limit = 50;

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: offset.toString()
      });
      if (eventType) params.append('event_type', eventType);

      const response = await fetch(`${ADMIN_API_URL}/audit-log?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to load audit logs');

      const data = await response.json();
      setLogs(data.logs);
      setTotal(data.total);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const checkIntegrity = async () => {
    try {
      const response = await fetch(`${ADMIN_API_URL}/audit-log/integrity`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to check integrity');
      const data = await response.json();
      setIntegrity(data);
    } catch (err) {
      setError(err.message);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [token, offset, eventType]);

  const getEventColor = (type) => {
    for (const [prefix, color] of Object.entries(EVENT_TYPE_COLORS)) {
      if (type.startsWith(prefix)) return color;
    }
    return 'bg-gray-600/20 text-gray-400';
  };

  const totalPages = Math.ceil(total / limit);
  const currentPage = Math.floor(offset / limit) + 1;

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Audit Log</h1>
          <p className="text-gray-400 text-sm">Immutable record of all admin actions</p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={checkIntegrity} 
            variant="outline" 
            size="sm" 
            className="border-gray-600"
            data-testid="check-integrity-btn"
          >
            <ShieldCheck className="w-4 h-4 mr-2" />
            Check Integrity
          </Button>
          <Button onClick={fetchLogs} variant="outline" size="sm" className="border-gray-600">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {error && (
        <Alert variant="destructive" className="bg-red-900/50 border-red-800">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {integrity && (
        <Alert className={integrity.integrity_percentage === 100 
          ? 'bg-green-900/30 border-green-700' 
          : 'bg-red-900/30 border-red-700'
        }>
          <ShieldCheck className={`h-4 w-4 ${
            integrity.integrity_percentage === 100 ? 'text-green-400' : 'text-red-400'
          }`} />
          <AlertDescription className={
            integrity.integrity_percentage === 100 ? 'text-green-200' : 'text-red-200'
          }>
            Integrity Check: {integrity.integrity_percentage.toFixed(1)}% valid 
            ({integrity.valid}/{integrity.checked} entries checked)
            {integrity.invalid > 0 && ` - ${integrity.invalid} invalid entries detected!`}
          </AlertDescription>
        </Alert>
      )}

      {/* Filters */}
      <div className="flex gap-4">
        <Select value={eventType || "all"} onValueChange={(v) => { setEventType(v === "all" ? "" : v); setOffset(0); }}>
          <SelectTrigger className="w-[200px] bg-gray-800 border-gray-700 text-white">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="All event types" />
          </SelectTrigger>
          <SelectContent className="bg-gray-800 border-gray-700">
            <SelectItem value="all">All events</SelectItem>
            <SelectItem value="admin">Admin actions</SelectItem>
            <SelectItem value="user">User actions</SelectItem>
            <SelectItem value="listing">Listing actions</SelectItem>
            <SelectItem value="payment">Payment actions</SelectItem>
            <SelectItem value="config">Config changes</SelectItem>
            <SelectItem value="ai">AI actions</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Logs Table */}
      <Card className="bg-gray-800 border-gray-700">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-700/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Timestamp</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Event</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Admin</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Target</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">IP</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {loading ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-gray-400">
                      <RefreshCw className="w-6 h-6 animate-spin mx-auto" />
                    </td>
                  </tr>
                ) : logs.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-gray-400">
                      No audit logs found
                    </td>
                  </tr>
                ) : (
                  logs.map((log, i) => (
                    <tr key={i} className="hover:bg-gray-700/50">
                      <td className="px-4 py-3 text-gray-300 text-sm whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <Clock className="w-4 h-4 text-gray-500" />
                          {new Date(log.timestamp).toLocaleString()}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <Badge className={getEventColor(log.event_type)}>
                          {log.event_type}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm">
                          <p className="text-white">{log.admin?.email}</p>
                          <p className="text-gray-500 text-xs capitalize">{log.admin?.role}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-400 text-sm">
                        {log.target?.type && (
                          <span>
                            {log.target.type}: {log.target.id?.slice(0, 12)}...
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-sm font-mono">
                        {log.ip_address}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {total > limit && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-gray-700">
              <p className="text-sm text-gray-400">
                Showing {offset + 1} to {Math.min(offset + limit, total)} of {total} entries
              </p>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setOffset(Math.max(0, offset - limit))}
                  disabled={offset === 0}
                  className="border-gray-600"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="px-3 py-1 text-gray-400">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setOffset(offset + limit)}
                  disabled={offset + limit >= total}
                  className="border-gray-600"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <p className="text-xs text-gray-500 text-center">
        Audit logs are immutable and cannot be edited or deleted.
      </p>
    </div>
  );
};

export default AdminAuditLogPage;
